/**
 * Módulo "Datos de empresa": punto de entrada único a las pantallas de
 * consulta — histórico de facturas tramitadas, maestros (proveedores y
 * clientes) y cuadros de financiación (leasings y préstamos).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, Users, Wallet, LogOut } from "lucide-react";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import EntryCard from "@/components/EntryCard";
import MasterSelector from "@/components/MasterSelector";
import { useAuth } from "@/contexts/AuthContext";

const DatosEmpresa = () => {
  const navigate = useNavigate();
  const { canAccessFinancing, signOut } = useAuth();
  const [showCompanySelector, setShowCompanySelector] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <BackButton variant="ghost" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 md:py-16">
        <div className="mb-10 md:mb-14">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight font-[Playfair_Display,serif]">
            Datos de empresa
          </h1>
          <p className="text-muted-foreground mt-2 text-base md:text-lg">
            Consulta el histórico, los maestros y los cuadros de financiación.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <EntryCard
            to="/historico-facturas"
            icon={History}
            title="Histórico de facturas"
            description="Consulta las facturas ya tramitadas y descarga los documentos."
          />
          <EntryCard
            onClick={() => setShowCompanySelector(true)}
            icon={Users}
            title="Maestros"
            description="Proveedores y clientes por empresa, con sus cuentas contables."
          />
          <EntryCard
            to={canAccessFinancing ? "/financiacion" : undefined}
            icon={Wallet}
            title="Financiación"
            description="Cuadros de leasings y préstamos por cliente, con capital pendiente."
            disabled={!canAccessFinancing}
            disabledHint="Sin acceso. Pide al administrador que te habilite Financiación."
          />
        </div>
      </main>

      <MasterSelector
        open={showCompanySelector}
        onSelect={(c) => {
          setShowCompanySelector(false);
          navigate(`/proveedores/${c.id}`);
        }}
        onCancel={() => setShowCompanySelector(false)}
      />
    </div>
  );
};

export default DatosEmpresa;
